import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
} from '@mui/material';
import { Formik, Field } from 'formik';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useModalState } from '../../hooks/useModalState';
import { FormikTextField, FormikSwitch } from '../../components/FormikFields';
import { conversionTypesService } from '../../services/conversion-types.service';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import FormDirtyTracker from '../../components/FormDirtyTracker';

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  event_keys_str: Yup.string().trim().required('At least one event key is required'),
  include_in_conversions: Yup.boolean().default(true),
  include_in_revenue: Yup.boolean().default(true),
});

export default function CreateEditConversionType() {
  const { modalState, toggleModal } = useModalState('conversion-type');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['conversion-type', modalState.id],
    queryFn: () => conversionTypesService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const isEdit = modalState.reason === 'Update' && !!entity;
  const isDuplicate = modalState.reason === 'Duplicate';
  const title = isEdit ? `Edit: ${entity?.name}` : isDuplicate ? 'Duplicate Conversion Type' : 'Create Conversion Type';

  const initialValues = (() => {
    if (!entity) return { name: '', event_keys_str: '', include_in_conversions: true, include_in_revenue: true, workspace_id: defaultCreateWorkspace() };
    const base = {
      name: entity.name,
      event_keys_str: (entity.event_keys || []).join(', '),
      include_in_conversions: entity.include_in_conversions,
      include_in_revenue: entity.include_in_revenue,
      workspace_id: (entity as any).workspace_id ?? defaultCreateWorkspace(),
    };
    return base;
  })();

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="sm" fullWidth title={isLoading ? 'Loading...' : title} titleId="conversion-type-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setSubmitted();
              const event_keys = values.event_keys_str
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(Boolean);
              const payload = {
                name: values.name,
                event_keys,
                include_in_conversions: values.include_in_conversions,
                include_in_revenue: values.include_in_revenue,
              };
              if (isEdit) {
                await conversionTypesService.update(entity!.id, payload);
              } else {
                await conversionTypesService.create({ ...payload, workspace_id: values.workspace_id });
              }
              queryClient.invalidateQueries({ queryKey: ['conversion-types'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status, values }: { submitForm: () => Promise<void>; isSubmitting: boolean; status?: string; values: { workspace_id: string } }) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CopyId id={entity?.id} show={isEdit} />
                  <Box sx={{ flex: 1 }} />
                  {isEdit && <HistoryButton entityType="conversion_types" entityId={entity!.id} entityName={entity?.name} />}
                </Box>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormikTextField name="name" label="Name" fullWidth size="small" placeholder="e.g. Purchase" />
                  {!isEdit && (
                    <Field name="workspace_id">
                      {({ field, form }: any) => (
                        <WorkspaceField value={field.value} onChange={(id) => form.setFieldValue('workspace_id', id)} />
                      )}
                    </Field>
                  )}
                  <FormikTextField
                    name="event_keys_str"
                    label="Event Keys"
                    fullWidth
                    size="small"
                    placeholder="e.g. purchase, sale, buy"
                    helperText="Comma-separated event keys matched against the et= postback param. Auto-lowercased."
                  />
                  <FormikSwitch name="include_in_conversions" label="Include in conversions count" />
                  <FormikSwitch name="include_in_revenue" label="Include in revenue" />
                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting || !values.workspace_id}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
